import React from 'react'
import { Box, Typography, Card, CardContent } from '@mui/material'
import { useTranslations } from 'next-intl'

import { RecentFSearch } from '@/src/utils/storage/recentSearch'
import { getFormattedDate } from '@/src/utils/date'

interface RecentSearchListProps {
    flightsSearchList: RecentFSearch[]
    onSearchClick: (searchQuery: RecentFSearch, url: string) => void
}

const RecentSearchList: React.FC<RecentSearchListProps> = ({ 
    flightsSearchList, 
    onSearchClick 
}) => {
    const t = useTranslations('homePage.headings')
    const tCommon = useTranslations('common')
    
    if (!flightsSearchList || flightsSearchList.length === 0) {
        return null
    }

    return (
        <Box sx={{ mb: 2, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '600px', md: '800px', lg: '1000px' } }}>
                <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
                    {t('recentSearches')}
                </Typography>
                
                <Box 
                    component="ul" 
                    sx={{ 
                        display: 'flex', 
                        gap: 1.25, 
                        listStyle: 'none', 
                        p: 0, 
                        m: 0,
                        overflowX: 'auto',
                        pb: 1,
                        justifyContent: 'flex-start',
                        flexWrap: 'wrap'
                    }}
                >
                {flightsSearchList.map((item, index) => {
                    if (!item) return null

                    const isLatestSearch = index === 0
                    const adults = Number(item.adults) || 0
                    const childs = Number(item.childs) || 0
                    const infants = Number(item.infants) || 0
                    const totalTraveler = adults + childs + infants
                    const isSinglePerson = totalTraveler < 2
                    const tripType = item.return_date ? tCommon('roundTrip') : tCommon('oneWay')
                    const travelClass = tCommon(item?.class) || ''
                    const departDate = getFormattedDate(
                        item.depart_date,
                        'ddd, D MMM',
                        'DD/MM/YYYY'
                    )
                    const returnDate = item.return_date ? getFormattedDate(
                        item.return_date,
                        'ddd, D MMM',
                        'DD/MM/YYYY'
                    ) : ''

                    // Generate URL safely
                    let url = '#'
                    try {
                        // This will be handled by the parent component
                        url = `#search-${index}`
                    } catch (urlError) {
                        console.error('Error generating URL:', urlError)
                    }

                    return (
                        <Box
                            key={`flight-search-${index}`}
                            component="li"
                            sx={{
                                minWidth: 280,
                                flex: '0 0 auto'
                            }}
                        >
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    position: 'relative',
                                    boxShadow: 'rgba(0, 0, 0, 0.04) 0px 8px 16px',
                                    border: '1px solid #e6e6e6',
                                    '&:hover': {
                                        boxShadow: 'rgba(0, 0, 0, 0.08) 0px 12px 20px',
                                        transform: 'translateY(-2px)',
                                        transition: 'all 0.2s ease-in-out'
                                    }
                                }}
                                onClick={() => onSearchClick(item, url)}
                            >
                                <CardContent sx={{ p: 2, pt: isLatestSearch ? 3.5 : 2 }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 1 }}
                                    >
                                        {tripType}
                                    </Typography>

                                    {item.sourceCountry && item.destinationCountry ? (
                                        <Typography
                                            variant="body1"
                                            sx={{ 
                                                color: 'text.primary',
                                                fontWeight: 500,
                                                mb: 1
                                            }}
                                        >
                                            {item.sourceCountry} → {item.destinationCountry}
                                        </Typography>
                                    ) : (
                                        <Typography
                                            variant="body1"
                                            sx={{ 
                                                color: 'text.primary',
                                                fontWeight: 500,
                                                mb: 1
                                            }}
                                        >
                                            {item.from} → {item.to}
                                        </Typography>
                                    )}

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 1 }}
                                    >
                                        {`${departDate}${returnDate?` - ${returnDate}`:''}`}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {`${travelClass} - ${totalTraveler} ${tCommon('traveller',{s : isSinglePerson ? '' : 's'})}`}
                                    </Typography>
                                </CardContent>

                                {/* Latest search badge */}
                                {isLatestSearch && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            left: 20,
                                            backgroundColor: '#3366CC',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{ 
                                                color: 'white',
                                                fontWeight: 500,
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {t('lastSearch')}
                                        </Typography>
                                    </Box>
                                )}
                            </Card>
                        </Box>
                    )
                })}
                </Box>
            </Box>
        </Box>
    )
}

export { RecentSearchList }
export type { RecentSearchListProps }
